import React, { useCallback, useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import TrainingPlanOutput from "./TrainingPlanOutput";
import { getPlan as generatePlan } from "./utils/planSwitcher";
import {
  formatPlanSavedDate,
  readSavedPlan,
  saveSavedPlan,
} from "./utils/savedPlanStorage";
import {
  computeWeeks,
  mondayOfWeekContaining,
  parseLocalCalendarDate,
} from "./utils/dateMath";
import "./PaceWorksForm.css";

function addDaysToIsoDate(isoDate, dayCount) {
  const d = parseLocalCalendarDate(isoDate);
  if (!d) return null;
  d.setDate(d.getDate() + dayCount);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

function formatRaceDateHint(isoDate) {
  const d = parseLocalCalendarDate(isoDate);
  if (!d) return isoDate;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getValidRaceDateRange(distance, intensity, startDate) {
  if (!distance || !intensity || !startDate) {
    return { minDate: null, maxDate: null };
  }

  const planStartMonday = mondayOfWeekContaining(startDate);
  if (!planStartMonday) {
    return { minDate: null, maxDate: null };
  }

  const minWeeksByDistance = {
    "5K": 4,
    "10K": 6,
    "10 Miler": 8,
    "Half Marathon": 8,
    Marathon: 16,
  };

  const maxWeeksByDistance = {
    "5K": 16,
    "10K": 20,
    "10 Miler": 24,
    "Half Marathon": 24,
    Marathon: 32,
  };

  const maxWeeksByDistanceEasy = {
    "5K": 12,
    "10K": 14,
    "10 Miler": 16,
    "Half Marathon": 18,
    Marathon: 32,
  };

  let minWeeks = minWeeksByDistance[distance];
  if (
    distance === "Half Marathon" &&
    (intensity === "Challenging" || intensity === "Intense")
  ) {
    minWeeks = 12;
  }

  const isEasyIntensity = intensity === "Easy";
  const maxWeeks = isEasyIntensity
    ? maxWeeksByDistanceEasy[distance]
    : maxWeeksByDistance[distance];

  if (typeof minWeeks !== "number" || typeof maxWeeks !== "number") {
    return { minDate: null, maxDate: null };
  }

  const minDate = addDaysToIsoDate(planStartMonday, (minWeeks - 1) * 7);
  const maxDate = addDaysToIsoDate(planStartMonday, (maxWeeks - 1) * 7);

  return { minDate, maxDate };
}

export default function PaceWorksForm() {
  const [formData, setFormData] = useState({
    raceDistance: "",
    raceDate: "",
    trainingStartDate: "",
    intensity: "",
    unitPreference: "Miles",
  });

  const [errors, setErrors] = useState({});
  const [planWeeks, setPlanWeeks] = useState([]);
  const [planIsReady, setPlanIsReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const hasGeneratedPlan = useRef(false);
  const trainingStartDateRef = useRef(null);
  const raceDateRef = useRef(null);
  const [dateWarning, setDateWarning] = useState("");
  const [includeCrossTraining, setIncludeCrossTraining] = useState(false);

  /** Drop stale plan + storage so old output is never shown after failed validation or form edits. */
  const clearGeneratedPlan = useCallback(() => {
    setPlanWeeks([]);
    setPlanIsReady(false);
    hasGeneratedPlan.current = false;
  }, []);

  const [showSavedPlanNotice, setShowSavedPlanNotice] = useState(false);
  const [savedPlanDateLabel, setSavedPlanDateLabel] = useState(null);
  const [formExpanded, setFormExpanded] = useState(false);

  useEffect(() => {
    const saved = readSavedPlan();
    if (!saved) {
      setShowSavedPlanNotice(false);
      setSavedPlanDateLabel(null);
      return;
    }

    setShowSavedPlanNotice(true);
    setSavedPlanDateLabel(formatPlanSavedDate(saved.savedAt));
    const fd = saved.formData;
    setFormData((prev) => ({
      ...prev,
      raceDistance: fd.raceDistance ?? prev.raceDistance,
      raceDate: fd.raceDate ?? prev.raceDate,
      trainingStartDate: fd.trainingStartDate ?? prev.trainingStartDate,
      intensity: fd.intensity ?? prev.intensity,
      unitPreference: fd.unitPreference ?? prev.unitPreference,
      startDate: fd.startDate,
      planWeeks: fd.planWeeks,
      totalWeeks: fd.totalWeeks,
    }));
    if (typeof fd.includeCrossTraining === "boolean") {
      setIncludeCrossTraining(fd.includeCrossTraining);
    }
    setPlanWeeks(saved.planWeeks);
    hasGeneratedPlan.current = true;
    setPlanIsReady(true);
  }, []);

  const openDatePicker = (inputRef) => {
    const el = inputRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      try {
        el.showPicker();
      } catch {
        el.focus();
      }
    } else {
      el.focus();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    clearGeneratedPlan();

    if (
      name === "raceDate" ||
      name === "trainingStartDate" ||
      name === "raceDistance" ||
      name === "intensity"
    ) {
      setDateWarning("");
    }
  };

  const setUnitPreference = (value) => {
    setFormData((prev) => ({ ...prev, unitPreference: value }));
    setErrors((prev) => ({ ...prev, unitPreference: "" }));
    clearGeneratedPlan();
  };

  const handleCrossTrainingChange = (e) => {
    setIncludeCrossTraining(e.target.checked);
    clearGeneratedPlan();
  };

  const validateForm = () => {
    const requiredFields = [
      "raceDistance",
      "raceDate",
      "trainingStartDate",
      "intensity",
      "unitPreference",
    ];
    const newErrors = {};
    for (const key of requiredFields) {
      if (!formData[key]) {
        newErrors[key] = "This field is required.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const intensityLabels = ["Easy", "Moderate", "Challenging", "Intense"];

  const distanceOptions = [
    "5K",
    "10K",
    "10 Miler",
    "Half Marathon",
    "Marathon",
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const startTime = Date.now();

    if (loading) return;
    if (!validateForm()) {
      clearGeneratedPlan();
      return;
    }

    const distance = formData.raceDistance;
    const raceDate = formData.raceDate;
    const planStartMonday = mondayOfWeekContaining(formData.trainingStartDate);

    if (!planStartMonday) {
      clearGeneratedPlan();
      setDateWarning("Please enter a valid start training date.");
      return;
    }

    if (planStartMonday > raceDate) {
      clearGeneratedPlan();
      setDateWarning(
        "Your plan cannot begin after the race. Adjust the start or race date."
      );
      return;
    }

    const totalWeeks = computeWeeks(planStartMonday, raceDate);

    const minWeeksByDistance = {
      "5K": 4,
      "10K": 6,
      "10 Miler": 8,
      "Half Marathon": 8,
      Marathon: 16,
    };

    const intensity = formData.intensity;
    let minWeeks = minWeeksByDistance[distance];
    if (
      distance === "Half Marathon" &&
      (intensity === "Challenging" || intensity === "Intense")
    ) {
      minWeeks = 12;
    }

    const maxWeeksByDistance = {
      "5K": 16,
      "10K": 20,
      "10 Miler": 24,
      "Half Marathon": 24,
      Marathon: 32,
    };

    /** Stricter caps for Easy-only unified volume plans (see planSwitcher). */
    const maxWeeksByDistanceEasy = {
      "5K": 12,
      "10K": 14,
      "10 Miler": 16,
      "Half Marathon": 18,
      Marathon: 32,
    };

    if (typeof minWeeks === "number" && totalWeeks < minWeeks) {
      clearGeneratedPlan();
      if (distance === "Marathon") {
        setDateWarning(
          "A marathon plan requires at least 16 weeks for safe training. Please choose an earlier start date."
        );
        return;
      }
      setDateWarning(
        `Your plan is ${totalWeeks} weeks, but a ${distance} plan requires at least ${minWeeks} weeks. Please choose an earlier start date or a shorter race distance.`
      );
      return;
    }

    const isEasyIntensity = intensity === "Easy";
    const maxWeeks = isEasyIntensity
      ? maxWeeksByDistanceEasy[distance]
      : maxWeeksByDistance[distance];
    if (typeof maxWeeks === "number" && totalWeeks > maxWeeks) {
      clearGeneratedPlan();
      if (isEasyIntensity) {
        setDateWarning(
          `Your selected dates would create a ${totalWeeks}-week plan. The maximum length for an Easy ${distance} plan is ${maxWeeks} weeks. Move your start date closer to your race date, or consider a Moderate intensity plan for longer training blocks.`
        );
      } else {
        setDateWarning(
          `Your selected dates would create a ${totalWeeks}-week plan. The maximum length for a ${distance} plan is ${maxWeeks} weeks. Move your start date closer to your race date to shorten the plan.`
        );
      }
      return;
    }

    const payload = {
      ...formData,
      includeCrossTraining,
      startDate: planStartMonday,
      planWeeks: totalWeeks,
      totalWeeks,
    };

    setFormData((prev) => ({
      ...prev,
      startDate: planStartMonday,
      planWeeks: totalWeeks,
      totalWeeks,
    }));
    setDateWarning("");

    setLoading(true);
    setPlanIsReady(false);
    hasGeneratedPlan.current = false;
    /** Defer heavy plan build so React can paint loading state before sync getPlan work. */
    window.setTimeout(() => {
      try {
        generatePlan(payload, (weeks) => {
          if (!weeks || !weeks.length) {
            console.error(
              "PaceWerks: no training weeks returned from plan generator."
            );
            clearGeneratedPlan();
            setDateWarning(
              "We could not build a plan for these selections. Try different dates or options, then generate again."
            );
            setLoading(false);
            return;
          }
          setPlanWeeks(weeks);
          saveSavedPlan(payload, weeks);
          setShowSavedPlanNotice(true);
          hasGeneratedPlan.current = true;
          setPlanIsReady(true);
          setFormExpanded(false);
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(0, 500 - elapsed);
          window.setTimeout(() => {
            setLoading(false);
            window.setTimeout(() => {
              document
                .querySelector(".pw-plan-output")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 100);
          }, remaining);
          if (typeof window.gtag === "function") {
            window.gtag("event", "plan_created", {
              event_category: "engagement",
              event_label: "Training Plan Created",
            });
          }
        });
      } catch (err) {
        console.error("PaceWerks: plan generation failed:", err);
        clearGeneratedPlan();
        setDateWarning(
          "We could not build a plan for these selections. Try different dates or options, then generate again."
        );
        setLoading(false);
      }
    }, 0);
  };

  const { minDate, maxDate } = getValidRaceDateRange(
    formData.raceDistance,
    formData.intensity,
    formData.trainingStartDate
  );

  const raceDateRangeHint =
    minDate && maxDate
      ? `Based on your selections, your race date should be between ${formatRaceDateHint(minDate)} and ${formatRaceDateHint(maxDate)}.`
      : null;

  const shouldCollapseForm = showSavedPlanNotice && planIsReady;

  return (
    <div className="pw-build-page">
      <div className="pw-build-card">
        <h1 className="pw-build-title">Build Your Plan</h1>

        {showSavedPlanNotice ? (
          <p className="pw-saved-plan-notice" role="status">
            You have a saved plan
            {savedPlanDateLabel ? ` from ${savedPlanDateLabel}` : ""}.{" "}
            <button
              type="button"
              className="pw-saved-plan-notice-link"
              onClick={() =>
                document
                  .querySelector(".pw-plan-output")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
            >
              View it
            </button>{" "}
            or build a new one below.
          </p>
        ) : null}

        {shouldCollapseForm ? (
          <div
            className="pw-form-collapsed-bar"
            role="button"
            tabIndex={0}
            onClick={() => setFormExpanded((open) => !open)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setFormExpanded((open) => !open);
              }
            }}
          >
            <span>Want to change something? Adjust your plan</span>
            <span className="pw-form-collapsed-chevron" aria-hidden="true">
              {formExpanded ? "▲" : "▼"}
            </span>
          </div>
        ) : null}

        {(!shouldCollapseForm || formExpanded) && (
        <div className={shouldCollapseForm ? "pw-form-expanded" : undefined}>
        <form className="pw-form" onSubmit={handleSubmit} noValidate>
          <div className="pw-field">
            <label htmlFor="raceDistance" className="pw-label">
              Race Distance
            </label>
            <select
              id="raceDistance"
              className="pw-select"
              name="raceDistance"
              value={formData.raceDistance}
              onChange={handleChange}
            >
              <option value="">Select...</option>
              {distanceOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.raceDistance && (
              <p className="pw-form-error">{errors.raceDistance}</p>
            )}
          </div>

          <div className="pw-field">
            <label htmlFor="intensity" className="pw-label">
              Desired Training Intensity
            </label>
            <select
              id="intensity"
              className="pw-select"
              name="intensity"
              value={formData.intensity}
              onChange={handleChange}
            >
              <option value="">Select...</option>
              {intensityLabels.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.intensity && (
              <p className="pw-form-error">{errors.intensity}</p>
            )}
          </div>

          <div className="pw-field">
            <label htmlFor="trainingStartDate" className="pw-label">
              Start Date
            </label>
            <div
              className="pw-date-wrapper"
              onClick={() => openDatePicker(trainingStartDateRef)}
            >
              <input
                ref={trainingStartDateRef}
                id="trainingStartDate"
                className="pw-date"
                type="date"
                name="trainingStartDate"
                value={formData.trainingStartDate}
                onChange={handleChange}
              />
            </div>
            {errors.trainingStartDate && (
              <p className="pw-form-error">{errors.trainingStartDate}</p>
            )}
            <p className="pw-field-hint">
              Pick any day in your first training week; the plan aligns to the
              Monday of that week through your race date.
            </p>
          </div>

          <div className="pw-field">
            <label htmlFor="raceDate" className="pw-label">
              Race Date
            </label>
            <div
              className="pw-date-wrapper"
              onClick={() => openDatePicker(raceDateRef)}
            >
              <input
                ref={raceDateRef}
                id="raceDate"
                className="pw-date"
                type="date"
                name="raceDate"
                value={formData.raceDate}
                onChange={handleChange}
                min={minDate || undefined}
                max={maxDate || undefined}
              />
            </div>
            {errors.raceDate && (
              <p className="pw-form-error">{errors.raceDate}</p>
            )}
            {raceDateRangeHint ? (
              <p className="pw-field-hint">{raceDateRangeHint}</p>
            ) : null}
          </div>

          <div className="pw-field">
            <span className="pw-label" id="unit-toggle-label">
              Units
            </span>
            <div
              className="pw-unit-toggle"
              role="group"
              aria-labelledby="unit-toggle-label"
            >
              <button
                type="button"
                className={
                  formData.unitPreference === "Miles"
                    ? "pw-unit-toggle--active"
                    : ""
                }
                onClick={() => setUnitPreference("Miles")}
              >
                Miles
              </button>
              <button
                type="button"
                className={
                  formData.unitPreference === "Kilometers"
                    ? "pw-unit-toggle--active"
                    : ""
                }
                onClick={() => setUnitPreference("Kilometers")}
              >
                Kilometers
              </button>
            </div>
            {errors.unitPreference && (
              <p className="pw-form-error">{errors.unitPreference}</p>
            )}
          </div>

          {dateWarning && (
            <p className="pw-date-warning" role="alert">
              {dateWarning}
            </p>
          )}

          <div className="pw-field pw-cross-training-field">
            <label
              className="pw-cross-training-label"
              htmlFor="includeCrossTraining"
            >
              <input
                id="includeCrossTraining"
                type="checkbox"
                className="pw-cross-training-checkbox"
                checked={includeCrossTraining}
                onChange={handleCrossTrainingChange}
              />
              <span>Add a cross training day to my plan</span>
            </label>
          </div>

          <button
            type="submit"
            className={`pw-submit-btn${loading ? " pw-submit-btn--loading" : ""}`}
            disabled={loading}
            aria-busy={loading || undefined}
          >
            {loading && (
              <span className="pw-submit-btn-spinner" aria-hidden="true" />
            )}
            {loading ? "Building your plan..." : "Generate Plan"}
          </button>
          <p className="pw-submit-disclaimer">
            By generating a plan you agree to our{" "}
            <Link className="pw-submit-disclaimer-link" to="/legal">
              Terms of Use
            </Link>
            . Always consult a physician before starting a new exercise
            program.
          </p>
        </form>
        </div>
        )}
      </div>

      {planIsReady && hasGeneratedPlan.current && (
        <div className="pw-plan-output-wrap pw-plan-output">
          <TrainingPlanOutput formData={formData} planWeeks={planWeeks} />
        </div>
      )}
    </div>
  );
}
